import i18n from '../i18n/config';

/**
 * BCP-47 tag to hand to `toLocaleDateString` / `toLocaleTimeString` /
 * `toLocaleString`, derived from the active UI language.
 *
 * Why this exists: every date in the app used to pass a hardcoded Arabic tag,
 * so an English-locale user read "١ أغسطس ٢٠٢٦" — Arabic-Indic digits and
 * Arabic month names — on an otherwise English screen.
 *
 * Arabic uses `ar-EG` rather than `ar-SA`. Both happen to render the same
 * Gregorian output in current Chromium, but a locale's default calendar is
 * implementation-defined and `ar-SA` is the one associated with the Islamic
 * calendar; pinning the Egypt tag keeps the product's primary market's
 * conventions and removes the ar-SA/ar-EG split that was scattered across the
 * admin screens.
 *
 * A table rather than a ternary: when French shipped, an `en ? en-US : ar-EG`
 * check sent French users straight back to Arabic-Indic digits — the very bug
 * described above. A new UI language now needs one row here, and forgetting it
 * falls back to Arabic rather than crashing.
 *
 * Read at call time, never cached — the user can switch language without a
 * reload.
 */
const LOCALE_TAGS = {
  ar: 'ar-EG',
  en: 'en-US',
  fr: 'fr-FR',
  // German shipped after this table was written and nobody added the row, so
  // every German date fell through to `ar-EG` — Arabic-Indic digits and Arabic
  // month names on a German screen. Precisely the failure the paragraph above
  // predicted, one language later.
  de: 'de-DE',
  // Spanish, 2026-10-08. `es-ES` rather than `es-419`: Intl formats both with
  // Latin digits and the same day-first order, and a member in Mexico reading
  // Spain's month names is a far smaller problem than the Arabic-Indic digits
  // a missing row produces.
  es: 'es-ES',
  // Turkish, Indonesian and Urdu, 2026-10-23. `ur-PK`, not `ur-IN`: Intl gives
  // Pakistan's tag Latin digits, which is what Pakistani apps and the rest of
  // this one use; `ur-IN` defaults to extended Arabic-Indic digits.
  tr: 'tr-TR',
  id: 'id-ID',
  ur: 'ur-PK',
};

export function dateLocale() {
  const base = (i18n.language || 'ar').toLowerCase().split(/[-_]/)[0];
  return LOCALE_TAGS[base] || LOCALE_TAGS.ar;
}

/** `toLocaleDateString` with the active UI locale. Empty string for no date. */
export function formatDate(value, options) {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(dateLocale(), options);
}

/** `toLocaleTimeString` with the active UI locale. Empty string for no date. */
export function formatTime(value, options) {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString(dateLocale(), options);
}

/** `toLocaleString` (date + time) with the active UI locale. */
export function formatDateTime(value, options) {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString(dateLocale(), options);
}

export default dateLocale;
